// src/helper/zReportPdf/renderToPdf.js
//
// Coordinate-based jsPDF renderer for Z-Reports.
// Uses embedded Noto Sans font to correctly render Cyrillic (RU/UK),
// Turkish (TR), German umlauts (DE), Latin-extended (ES/IT/CZE), and more.
// No html2canvas dependency — synchronous, predictable output.

import { getFormattedCurrency, getDistanceInKm, getNeededDateFormat } from '../index'
import { ensurePdfFonts, __resetPdfFontsCache } from '../pdfFonts'

// Re-export the shared cache reset under the legacy name so existing zReportPdf
// infra tests keep working without changes.
// eslint-disable-next-line no-underscore-dangle
export const __resetFontsForTest = __resetPdfFontsCache

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MARGIN_L = 40        // left margin px
const MARGIN_R = 40        // right margin px
const PAGE_W = 595         // A4 width px
const PAGE_H = 842         // A4 height px
const MARGIN_BOTTOM = 40   // bottom margin px
const MARGIN_TOP = 50      // top margin on new page
const LINE_H = 16          // standard row height
const SECTION_GAP = 10     // gap before new section

const COLOR_BLACK = '#000000'
const COLOR_GRAY = '#333333'
const COLOR_LIGHT_GRAY = '#cccccc'

// Middle (gross) column x for the 3-column VAT-breakdown rows; the VAT column
// reuses the standard right margin (PAGE_W - MARGIN_R).
const TAX_GROSS_COL_X = 440

// Platform/fee item types shown inside the per-payment VAT breakdown, but only
// when non-zero (mirrors the on-screen modal filter).
const PAYMENT_TAX_FEE_TYPES = [
	'PLATFORM_FEE', 'TAKEAWAY_PLATFORM_FEE', 'UBER_EAT_PLATFORM_FEE',
	'FOODORA_PLATFORM_FEE', 'DELIVERY_COST', 'WOLT_PLATFORM_FEE', 'SMALL_ORDER_FEE',
]

// ---------------------------------------------------------------------------
// Font registration (runs once per pdf instance)
// ---------------------------------------------------------------------------
async function registerFonts(pdf) {
	const { regular, bold } = await ensurePdfFonts()
	pdf.addFileToVFS('NotoSans-Regular.ttf', regular)
	pdf.addFileToVFS('NotoSans-Bold.ttf', bold)
	pdf.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal')
	pdf.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold')
}

// ---------------------------------------------------------------------------
// Pagination helper
// ---------------------------------------------------------------------------
function ensureSpace(pdf, y, needed = LINE_H) {
	if (y + needed > PAGE_H - MARGIN_BOTTOM) {
		pdf.addPage()
		return MARGIN_TOP
	}
	return y
}

// ---------------------------------------------------------------------------
// Tiny drawing helpers
// ---------------------------------------------------------------------------
function setNormal(pdf, size = 10) {
	pdf.setFont('NotoSans', 'normal')
	pdf.setFontSize(size)
}

function setBold(pdf, size = 10) {
	pdf.setFont('NotoSans', 'bold')
	pdf.setFontSize(size)
}

function drawHRule(pdf, y, color = COLOR_BLACK) {
	pdf.setDrawColor(color)
	pdf.setLineWidth(0.5)
	pdf.line(MARGIN_L, y, PAGE_W - MARGIN_R, y)
}

/**
 * Draw a label-value row.
 * @param {object} pdf
 * @param {string} label
 * @param {string} value
 * @param {number} y
 * @param {boolean} bold
 */
function drawRow(pdf, label, value, y, bold = false) {
	if (bold) {
		setBold(pdf)
	} else {
		setNormal(pdf)
	}
	pdf.text(label, MARGIN_L, y)
	pdf.text(String(value), PAGE_W - MARGIN_R, y, { align: 'right' })
}

function formatMoney(value, currencyLocale, currencyCode) {
	return getFormattedCurrency(Number(value) || 0, currencyLocale, currencyCode)
}

/**
 * Draw a 3-column row: label (left) + gross (mid, right-aligned) + vat (right).
 * Used by the per-payment-method VAT breakdown.
 */
function drawTripleRow(pdf, label, midValue, rightValue, y, bold = false) {
	if (bold) {
		setBold(pdf)
	} else {
		setNormal(pdf)
	}
	pdf.text(label, MARGIN_L, y)
	pdf.text(String(midValue), TAX_GROSS_COL_X, y, { align: 'right' })
	pdf.text(String(rightValue), PAGE_W - MARGIN_R, y, { align: 'right' })
}

// ---------------------------------------------------------------------------
// Section renderers — each returns updated y position
// ---------------------------------------------------------------------------

function renderTitle(pdf, report, t, y) {
	setBold(pdf, 16)
	pdf.text(`${t('Reports.ZReports.pdf.title')} #${report.zNumber}`, MARGIN_L, y)
	return y + 20
}

function renderMeta(pdf, report, t, y) {
	const period = `${getNeededDateFormat(report.timeStart, 'dd.MM.yyyy HH:mm')} \u2013 ${getNeededDateFormat(report.timeEnd, 'dd.MM.yyyy HH:mm')}`
	const performed = getNeededDateFormat(report.timeEnd, 'dd.MM.yyyy HH:mm')
	setNormal(pdf, 9)
	pdf.setTextColor(COLOR_GRAY)
	pdf.text(`${t('Reports.ZReports.modal.performed')} ${performed} \u00B7 ${period}`, MARGIN_L, y)
	pdf.setTextColor(COLOR_BLACK)
	return y + 14
}

function renderCompanyBlock(pdf, company, y) {
	drawHRule(pdf, y)
	y += 10
	setBold(pdf, 10)
	pdf.text(company.name || '', MARGIN_L, y)
	y += LINE_H
	setNormal(pdf, 9)
	const phone = `Tel. (${company.phonePrefix || ''})${company.phoneNumber || ''}`
	const faxPart = company.fax ? ` | Fax. ${company.fax}` : ''
	const taxPart = ` | St-Nr. ${company.taxId || ''}`
	pdf.text(
		`${company.street || ''} ${company.house || ''} | ${phone}${faxPart}${taxPart}`,
		MARGIN_L, y
	)
	y += 10
	drawHRule(pdf, y)
	return y + SECTION_GAP
}

function renderOrderTotalsSection(pdf, report, t, currencyLocale, currencyCode, y) {
	const EXCLUDED = [
		'MAIN_DISH', 'MENU', 'TOPPING', 'BOTTLE_DEPOSIT', 'TOBACCO', 'BASIC_FOOD', 'DISCOUNT',
		'EXTRA_DISCOUNT', 'PLATFORM_FEE', 'TAKEAWAY_PLATFORM_FEE', 'UBER_EAT_PLATFORM_FEE',
		'FOODORA_PLATFORM_FEE', 'WOLT_PLATFORM_FEE', 'MARKETING_COUPON', 'MAILING_COUPON',
		'ONE_TIME_COUPON', 'SMALL_ORDER_FEE',
	]
	const otherTotals = (report.itemTypeTotals || []).filter(it => !EXCLUDED.includes(it.itemType))
	const discountEntry = (report.itemTypeTotals || []).find(it => it.itemType === 'DISCOUNT')
	const bottleDepositEntry = (report.itemTypeTotals || []).find(it => it.itemType === 'BOTTLE_DEPOSIT')
	const tobaccoEntry = (report.itemTypeTotals || []).find(it => it.itemType === 'TOBACCO')
	const basicFoodEntry = (report.itemTypeTotals || []).find(it => it.itemType === 'BASIC_FOOD')

	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.orders')}:`, report.ordersCount + report.canceledOrdersCount, y)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.canceled_orders')}:`, report.canceledOrdersCount, y)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.article')}:`, formatMoney(report.productsTotal, currencyLocale, currencyCode), y)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.item.MARKETING_COUPON')}:`, formatMoney(report.couponsTotal, currencyLocale, currencyCode), y)
	y += LINE_H

	for (const it of otherTotals) {
		y = ensureSpace(pdf, y)
		drawRow(pdf, `${t('Reports.ZReports.modal.item.' + it.itemType)}:`, formatMoney(it.total, currencyLocale, currencyCode), y)
		y += LINE_H
	}

	if (discountEntry) {
		y = ensureSpace(pdf, y)
		drawRow(pdf, `${t('Reports.ZReports.modal.item.DISCOUNT')}:`, formatMoney(report.aggregateDiscountTotal, currencyLocale, currencyCode), y)
		y += LINE_H
	}
	if (bottleDepositEntry) {
		y = ensureSpace(pdf, y)
		drawRow(pdf, `${t('Reports.ZReports.modal.item.BOTTLE_DEPOSIT')}:`, formatMoney(bottleDepositEntry.total, currencyLocale, currencyCode), y)
		y += LINE_H
	}
	if (basicFoodEntry) {
		y = ensureSpace(pdf, y)
		drawRow(pdf, `${t('Reports.ZReports.modal.item.BASIC_FOOD')}:`, formatMoney(basicFoodEntry.total, currencyLocale, currencyCode), y)
		y += LINE_H
	}
	if (tobaccoEntry) {
		y = ensureSpace(pdf, y)
		drawRow(pdf, `${t('Reports.ZReports.modal.item.TOBACCO')}:`, formatMoney(tobaccoEntry.total, currencyLocale, currencyCode), y)
		y += LINE_H
	}

	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.distance')}:`, `${getDistanceInKm(report.distanceTotal)} Km`, y)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.phone')}:`, formatMoney(report.noPlatformTotal, currencyLocale, currencyCode), y)
	y += LINE_H

	y = ensureSpace(pdf, y)
	drawHRule(pdf, y, COLOR_BLACK)
	// LINE_H gap (not magic 8) so jsPDF text bbox above baseline doesn't
	// overlap the rule — fixes BA "съезжают строки" report 2026-05-07.
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.all_total_price')}:`, formatMoney(report.totalPrice + report.canceledTotalPrice, currencyLocale, currencyCode), y, true)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.canceled_total_price')}:`, formatMoney(-report.canceledTotalPrice, currencyLocale, currencyCode), y)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.total_price')}:`, formatMoney(report.totalPrice, currencyLocale, currencyCode), y, true)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.invoice_total')}:`, formatMoney(report.invoiceTotal, currencyLocale, currencyCode), y)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.paypal_total')}:`, formatMoney(report.payPalTotal || 0, currencyLocale, currencyCode), y)
	y += LINE_H

	drawHRule(pdf, y, COLOR_LIGHT_GRAY)
	return y + SECTION_GAP
}

function renderPaymentMethodsSection(pdf, report, t, currencyLocale, currencyCode, y) {
	const METHODS = [
		{ method: 'EC',          labelKey: 'Reports.ZReports.modal.EC',          getTotal: (r) => (r.paymentTotals || []).find(pt => pt.method === 'EC' && !pt.paymentName)?.total || 0 },
		{ method: 'ONLINE',      labelKey: 'Reports.ZReports.modal.online',       getTotal: (r) => (r.paymentTotals || []).find(pt => pt.method === 'ONLINE' && !pt.paymentName)?.total || 0 },
		{ method: 'CASH',        labelKey: 'Reports.ZReports.modal.cash',         getTotal: (r) => r.cashTotal || 0 },
		{ method: 'GIFT_COUPON', labelKey: 'Reports.ZReports.modal.gift_coupon',  getTotal: (r) => (r.paymentTotals || []).find(pt => pt.method === 'GIFT_COUPON' && !pt.paymentName)?.total || 0 },
	]

	for (const { method, labelKey, getTotal } of METHODS) {
		y = ensureSpace(pdf, y)
		setBold(pdf, 10)
		const total = getTotal(report)
		pdf.text(`${t(labelKey)}: ${formatMoney(total, currencyLocale, currencyCode)}`, MARGIN_L, y)
		y += LINE_H

		// Per-orderType breakdown
		for (const ot of (report.orderTypePaymentTotals || [])) {
			const pt = (ot.paymentTotals || []).find(p => p.method === method && !p.paymentName)
			if (!pt) continue
			y = ensureSpace(pdf, y)
			setNormal(pdf)
			drawRow(pdf, `${t('Reports.ZReports.modal.type.' + ot.orderType)} ${t(labelKey)}:`, formatMoney(pt.total, currencyLocale, currencyCode), y)
			y += LINE_H
		}

		// Per-platform breakdown
		for (const pp of (report.platformPaymentTotals || [])) {
			y = ensureSpace(pdf, y)
			setNormal(pdf)
			pdf.text(`${t('Reports.ZReports.modal.platform.' + pp.platformType)} ${t(labelKey)}:`, MARGIN_L, y)
			y += LINE_H
			for (const rest of (pp.restaurantTotals || [])) {
				const pt = (rest.paymentTotals || []).find(p => p.method === method)
				if (!pt) continue
				y = ensureSpace(pdf, y)
				drawRow(pdf, `  ${rest.restaurant}`, formatMoney(pt.total, currencyLocale, currencyCode), y)
				y += LINE_H
			}
		}
	}

	drawHRule(pdf, y, COLOR_LIGHT_GRAY)
	return y + SECTION_GAP
}

/**
 * Renders the per-tax-rate detail rows for one payment method / custom payment.
 * Mirrors the on-screen modal (zreport/index.jsx + CustomPaymentTaxes):
 *   Artikel, Rabatt, configurable coupon types (always shown), TIPS and fees
 *   (only when non-zero). Returns the updated y.
 */
function renderTaxRows(pdf, tax, t, currencyLocale, currencyCode, y, { couponTypes, includeFees }) {
	const items = tax.itemTypeTaxes || []

	y = ensureSpace(pdf, y)
	drawTripleRow(pdf, `${t('Reports.ZReports.modal.article')} :`, formatMoney(tax.totalProductsGross, currencyLocale, currencyCode), formatMoney(tax.totalProductsVat, currencyLocale, currencyCode), y)
	y += LINE_H

	y = ensureSpace(pdf, y)
	drawTripleRow(pdf, `${t('Reports.ZReports.modal.item.DISCOUNT')} :`, formatMoney(tax.aggregateDiscountGross, currencyLocale, currencyCode), formatMoney(tax.aggregateDiscountVat, currencyLocale, currencyCode), y)
	y += LINE_H

	// Coupons: shown whenever present (no zero-filter), in declared order.
	for (const couponType of couponTypes) {
		for (const item of items.filter(it => it?.itemType === couponType)) {
			y = ensureSpace(pdf, y)
			drawTripleRow(pdf, `${t('Reports.ZReports.modal.item.' + couponType)} :`, formatMoney(item.totalGross, currencyLocale, currencyCode), formatMoney(item.totalVat, currencyLocale, currencyCode), y)
			y += LINE_H
		}
	}

	// TIPS: only when non-zero.
	for (const item of items.filter(it => it?.itemType === 'TIPS' && (it.totalGross !== 0 || it.totalVat !== 0))) {
		y = ensureSpace(pdf, y)
		drawTripleRow(pdf, `${t('Reports.ZReports.modal.item.TIPS')} :`, formatMoney(item.totalGross, currencyLocale, currencyCode), formatMoney(item.totalVat, currencyLocale, currencyCode), y)
		y += LINE_H
	}

	// Platform/delivery fees: only when non-zero, default methods only.
	if (includeFees) {
		for (const item of items.filter(it => PAYMENT_TAX_FEE_TYPES.includes(it?.itemType) && (it.totalGross !== 0 || it.totalVat !== 0))) {
			y = ensureSpace(pdf, y)
			drawTripleRow(pdf, `${t('Reports.ZReports.modal.item.' + item.itemType)} :`, formatMoney(item.totalGross, currencyLocale, currencyCode), formatMoney(item.totalVat, currencyLocale, currencyCode), y)
			y += LINE_H
		}
	}

	return y
}

/**
 * Per-payment-method VAT breakdown (Bar/Online/EC MwSt … Gesamt Bar, plus custom
 * payment methods). Mirrors zreport/index.jsx:250-309 + CustomPaymentTaxes.
 * This block was missing from the PDF (LP-5669).
 */
function renderPaymentMethodTaxesSection(pdf, report, t, currencyLocale, currencyCode, y) {
	const all = report.paymentTaxes || []
	const defaults = all.filter(pt => !pt.paymentName)
	const customs = all.filter(pt => pt.paymentName)
	if (defaults.length === 0 && customs.length === 0) return y

	const renderEntry = (entry, headerLabel, rowOpts) => {
		for (const tax of (entry.taxes || [])) {
			// Mirror the screen: a missing rate renders as empty, never "undefined%".
			const taxRate = tax.taxRate ?? ''
			y = ensureSpace(pdf, y)
			drawTripleRow(pdf, `${headerLabel} ${t('Reports.ZReports.modal.vat')} ${taxRate}% :`, `${taxRate}%:`, t('Reports.ZReports.modal.vat'), y, true)
			y += LINE_H
			y = renderTaxRows(pdf, tax, t, currencyLocale, currencyCode, y, rowOpts)
		}
		y = ensureSpace(pdf, y)
		drawRow(pdf, `${t('Reports.ZReports.modal.total')} ${headerLabel}:`, formatMoney(entry.vatTotal, currencyLocale, currencyCode), y, true)
		y += LINE_H
		// Light separator between each payment-method block for readability (BA LP-5669).
		y = ensureSpace(pdf, y)
		drawHRule(pdf, y, COLOR_LIGHT_GRAY)
		y += SECTION_GAP
	}

	for (const entry of defaults) {
		renderEntry(entry, t(`Reports.ZReports.modal.method.${entry.method}`), {
			couponTypes: ['MARKETING_COUPON', 'MAILING_COUPON', 'ONE_TIME_COUPON', 'GIFT_COUPON'],
			includeFees: true,
		})
	}
	for (const entry of customs) {
		renderEntry(entry, entry.paymentName, {
			couponTypes: ['GIFT_COUPON'],
			includeFees: false,
		})
	}

	// renderEntry already closes the last block with a light separator.
	return y
}

function renderTaxesSection(pdf, report, t, currencyLocale, currencyCode, y) {
	y = ensureSpace(pdf, y)
	setBold(pdf, 10)
	pdf.text(t('Reports.ZReports.modal.total_tax'), MARGIN_L, y)
	y += LINE_H

	for (const tax of (report.taxes || [])) {
		y = ensureSpace(pdf, y)
		drawRow(pdf, `${t('Reports.ZReports.modal.turnover')} ${tax.taxRate}%:`, formatMoney(tax.totalGross, currencyLocale, currencyCode), y, true)
		y += LINE_H
		y = ensureSpace(pdf, y)
		drawRow(pdf, `${t('Reports.ZReports.modal.tax')} ${tax.taxRate}%:`, formatMoney(tax.totalVat, currencyLocale, currencyCode), y, true)
		y += LINE_H
	}

	y = ensureSpace(pdf, y)
	drawHRule(pdf, y, COLOR_BLACK)
	y += LINE_H
	y = ensureSpace(pdf, y)
	drawRow(pdf, `${t('Reports.ZReports.modal.total_tax')}:`, formatMoney(report.taxesVatTotal, currencyLocale, currencyCode), y, true)
	y += LINE_H

	drawHRule(pdf, y, COLOR_LIGHT_GRAY)
	return y + SECTION_GAP
}

function renderCashBalanceSection(pdf, report, reportCashData, t, currencyLocale, currencyCode, y) {
	if (!reportCashData) return y
	const hasAny =
		!isNaN(reportCashData.initialCashBalance) ||
		!isNaN(reportCashData.cashReceived) ||
		!isNaN(reportCashData.cashExpenses) ||
		!isNaN(reportCashData.closingCashBalance)
	if (!hasAny) return y

	setBold(pdf, 10)
	pdf.text(t('Reports.ZReports.modal.cash_balance'), MARGIN_L, y)
	y += LINE_H

	if (!isNaN(reportCashData.initialCashBalance)) {
		drawRow(pdf, `${t('Reports.ZReports.modal.cash_balance')}:`, formatMoney(reportCashData.initialCashBalance, currencyLocale, currencyCode), y)
		y += LINE_H
	}
	if (!isNaN(reportCashData.cashReceived)) {
		drawRow(pdf, `${t('Reports.ZReports.modal.cash_received')}:`, formatMoney(reportCashData.cashReceived + report.cashTotal, currencyLocale, currencyCode), y)
		y += LINE_H
	}
	if (!isNaN(reportCashData.cashExpenses)) {
		drawRow(pdf, `${t('Reports.ZReports.modal.cash_expenses')}:`, formatMoney(reportCashData.cashExpenses, currencyLocale, currencyCode), y)
		y += LINE_H
	}
	if (!isNaN(reportCashData.closingCashBalance)) {
		drawRow(pdf, `${t('Reports.ZReports.modal.closing_cash_balance')}:`, formatMoney(reportCashData.closingCashBalance + report.cashTotal, currencyLocale, currencyCode), y, true)
		y += LINE_H
	}

	drawHRule(pdf, y, COLOR_LIGHT_GRAY)
	return y + SECTION_GAP
}

function renderFooter(pdf, t, y) {
	setNormal(pdf, 8)
	pdf.setTextColor(COLOR_GRAY)
	pdf.text(`${t('Reports.ZReports.pdf.unofficialCopy')} \u2014 ${new Date().toLocaleString()}`, PAGE_W / 2, y, { align: 'center' })
	pdf.setTextColor(COLOR_BLACK)
	return y + LINE_H
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------
/**
 * Renders a Z-Report into an existing jsPDF instance using direct text/line API
 * with embedded Noto Sans font (Cyrillic + Latin + Turkish support).
 *
 * @param {import('jspdf').jsPDF} pdf  — jsPDF instance (already constructed, A4)
 * @param {object} report              — ZReport data
 * @param {object|null} reportCashData — Cash balance data (optional)
 * @param {object} company             — Company info
 * @param {function} t                 — i18n translate function
 * @param {string} currencyLocale      — e.g. 'de-DE'
 * @param {string} currencyCode        — e.g. 'EUR'
 * @returns {import('jspdf').jsPDF}    — same pdf instance (fluent)
 */
export async function renderToPdf(pdf, report, reportCashData, company, t, currencyLocale, currencyCode) {
	await registerFonts(pdf)

	let y = 50

	y = renderTitle(pdf, report, t, y)
	y = renderMeta(pdf, report, t, y)
	y = renderCompanyBlock(pdf, company, y)
	y = renderOrderTotalsSection(pdf, report, t, currencyLocale, currencyCode, y)
	y = renderPaymentMethodsSection(pdf, report, t, currencyLocale, currencyCode, y)
	y = renderPaymentMethodTaxesSection(pdf, report, t, currencyLocale, currencyCode, y)
	y = renderTaxesSection(pdf, report, t, currencyLocale, currencyCode, y)
	y = renderCashBalanceSection(pdf, report, reportCashData, t, currencyLocale, currencyCode, y)
	renderFooter(pdf, t, y)

	return pdf
}
